'use client';

/**
 * /{username}/reservar — Public booking page (competitor to Calendly).
 *
 * SPEC-171 (Sprint 85, 2026-09-10): Internal reservation system.
 * PENDIENTE-FOUNDER RESUELTO 2026-09-10: SPEC-NEGOCIO §12 (Sistema de Reservas) — ver docs/SPEC-NEGOCIO-LOGICA-VIVA.md §12.
 *
 * Shows the host's availability + lets visitors book a 30 or 60 min slot.
 * On booking, a Conference Room is auto-created + the guest gets the link.
 *
 * §6 Anti-Invención: pure UI — all logic is in the backend service.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { Calendar, Clock, Video, Check, Loader2, ArrowLeft, MapPin, Monitor } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api, ApiClientError, type ReservationAvailability, type ReservationSlot, type ReservationCreated } from '@/lib/api-client';
import { useAuth } from '@/lib/use-auth';

export default function BookingPage() {
  const params = useParams();
  const router = useRouter();
  const username = params.username as string;
  const { user } = useAuth();

  const [availability, setAvailability] = useState<ReservationAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDuration, setSelectedDuration] = useState<number>(30);
  const [slots, setSlots] = useState<ReservationSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<ReservationSlot | null>(null);
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState<ReservationCreated | null>(null);

  // PR #1435 — when the host has BOTH modes active simultaneously, the
  // visitor picks one before booking. `selectedMode` is null until the
  // visitor picks (only required when both modes are active — when only
  // one mode is active, the backend defaults to it + this state is unused).
  const [selectedMode, setSelectedMode] = useState<'digital' | 'presencial' | null>(null);

  // Guest info form
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    document.title = `Reservar con @${username} | Profileia`;
  }, [username]);

  // Pre-fill guest info if authenticated
  useEffect(() => {
    if (user) {
      setGuestName(user.profile?.name || user.username);
      setGuestEmail(user.email);
    }
  }, [user]);

  const fetchAvailability = useCallback(async () => {
    try {
      setLoading(true);
      const avail = await api.reservations.getPublicAvailability(username);
      setAvailability(avail);
      if (avail && avail.durations.length > 0) {
        setSelectedDuration(avail.durations[0]);
      }
    } catch (err) {
      // 404 handled by getPublicAvailability returning null
    } finally {
      setLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const fetchSlots = useCallback(async () => {
    if (!availability) return;
    try {
      const result = await api.reservations.getSlots(username, selectedDuration);
      setSlots(result);
    } catch (err) {
      if (err instanceof ApiClientError) {
        toast.error(err.message);
      }
      setSlots([]);
    }
  }, [username, selectedDuration, availability]);

  useEffect(() => {
    if (availability) fetchSlots();
  }, [fetchSlots]);

  const handleBook = async () => {
    if (!selectedSlot) return;
    if (!guestName.trim() || !guestEmail.trim()) {
      toast.error('Nombre y email son obligatorios');
      return;
    }
    // PR #1435 — if the host has both modes active, the visitor MUST pick
    // one before booking. If only one mode is active, `selectedMode` is
    // null + we don't send the field (backend defaults to the host's only mode).
    const hostModes = availability?.modes ?? (availability ? [availability.mode] : []);
    const bothModesActive = hostModes.includes('digital') && hostModes.includes('presencial');
    if (bothModesActive && !selectedMode) {
      toast.error('Selecciona una modalidad', { description: 'Elige entre Digital o Presencial antes de reservar.' });
      return;
    }
    setBooking(true);
    try {
      const result = await api.reservations.book(username, {
        guestName: guestName.trim(),
        guestEmail: guestEmail.trim(),
        guestPhone: guestPhone.trim() || undefined,
        startTime: selectedSlot.startTime,
        durationMinutes: selectedSlot.durationMinutes,
        notes: notes.trim() || undefined,
        // PR #1435 — send the visitor-selected mode ONLY when both modes
        // are active. When only one mode is active, omit the field (the
        // backend defaults to the host's only mode).
        ...(bothModesActive && selectedMode ? { mode: selectedMode } : {}),
      });
      setConfirmed(result);
      // V124.2-RESERVAS-TOGGLE-DIGITAL-PRESENCIAL: toast distinto por modalidad.
      if (result.mode === 'presencial') {
        toast.success('Reserva presencial confirmada. Revisa la ubicación abajo.');
      } else {
        toast.success('Reserva confirmada. Se creó una sala para tu reunión.');
      }
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'Error al reservar';
      toast.error('No se pudo reservar', { description: msg });
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Reservation not enabled
  if (!availability || !availability.enabled) {
    return (
      <div className="container mx-auto py-16 px-4 max-w-md text-center">
        <Calendar className="size-12 mx-auto text-muted-foreground mb-4" />
        <h1 className="text-xl font-bold mb-2">Reservas no disponibles</h1>
        <p className="text-sm text-muted-foreground mb-6">
          @{username} aún no ha activado su calendario de reservas.
        </p>
        <Button variant="outline" onClick={() => router.push(`/${username}`)}>
          <ArrowLeft className="size-4 mr-2" />
          Volver al perfil
        </Button>
      </div>
    );
  }

  // Confirmation screen
  if (confirmed) {
    const isPresencial = confirmed.mode === 'presencial';
    return (
      <div className="container mx-auto py-16 px-4 max-w-md text-center">
        <div className="size-16 mx-auto rounded-full bg-green-500/10 flex items-center justify-center mb-4">
          <Check className="size-8 text-green-500" />
        </div>
        <h1 className="text-xl font-bold mb-2">¡Reserva confirmada!</h1>
        <p className="text-sm text-muted-foreground mb-6">
          {isPresencial
            ? `Tu reserva presencial con @${username} está confirmada. Te esperamos en el lugar indicado.`
            : `Tu reserva con @${username} está confirmada. Se creó una sala de conferencia para tu reunión.`}
        </p>
        <Card className="text-left mb-6">
          <CardContent className="p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Fecha:</span>
              <span className="font-medium">{new Date(confirmed.startTime).toLocaleString('es-CO')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Duración:</span>
              <span className="font-medium">{confirmed.durationMinutes} min</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Modalidad:</span>
              <span className="font-medium flex items-center gap-1.5">
                {isPresencial ? (
                  <>
                    <MapPin className="size-3.5" aria-hidden />
                    Presencial
                  </>
                ) : (
                  <>
                    <Monitor className="size-3.5" aria-hidden />
                    Digital
                  </>
                )}
              </span>
            </div>
            {isPresencial && confirmed.location && (
              <div className="pt-2 mt-2 border-t">
                <p className="text-xs text-muted-foreground mb-1">Ubicación:</p>
                <p className="text-sm font-medium flex items-start gap-1.5">
                  <MapPin className="size-4 text-amber-500 shrink-0 mt-0.5" aria-hidden />
                  <span>{confirmed.location}</span>
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  El host fue notificado de tu reserva. Te esperamos en el lugar acordado a la hora indicada.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        <div className="flex flex-col gap-2">
          {isPresencial ? (
            <Button variant="outline" onClick={() => router.push(`/${username}`)}>
              <ArrowLeft className="size-4 mr-2" />
              Volver al perfil
            </Button>
          ) : (
            <>
              <Button onClick={() => router.push(`/app/conferences/${confirmed.roomId}`)}>
                <Video className="size-4 mr-2" />
                Ir a la sala
              </Button>
              <Button variant="outline" onClick={() => router.push(`/${username}`)}>
                Volver al perfil
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Booking form
  return (
    <div className="container mx-auto py-8 px-4 max-w-2xl">
      <Button variant="ghost" size="sm" onClick={() => router.push(`/${username}`)} className="mb-4">
        <ArrowLeft className="size-4 mr-2" />
        Volver al perfil
      </Button>

      <h1 className="text-2xl font-bold mb-1">Reservar con @{username}</h1>
      <p className="text-sm text-muted-foreground mb-6">
        {/* PR #1435 — read from `modes` array (source of truth). */}
        {(() => {
          const modes = availability.modes ?? [availability.mode];
          if (modes.includes('digital') && modes.includes('presencial')) {
            return 'Selecciona una modalidad y un horario disponible.';
          }
          if (modes.includes('presencial')) {
            return 'Selecciona un horario disponible para tu reserva presencial.';
          }
          return 'Selecciona un horario disponible. Se creará automáticamente una sala de conferencia para tu reunión.';
        })()}
      </p>

      {/* PR #1435 — mode picker (only when BOTH modes are active). */}
      {(() => {
        const modes = availability.modes ?? [availability.mode];
        const bothActive = modes.includes('digital') && modes.includes('presencial');
        if (!bothActive) return null;
        return (
          <div className="mb-6 rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 space-y-3">
            <div>
              <p className="text-sm font-medium text-foreground">Elige la modalidad de tu reserva</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Este anfitrión ofrece ambas modalidades. Selecciona cuál prefieres para esta reserva.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {(['digital', 'presencial'] as const).map(m => {
                const active = selectedMode === m;
                const Icon = m === 'digital' ? Monitor : MapPin;
                const label = m === 'digital' ? 'Digital' : 'Presencial';
                const description = m === 'digital'
                  ? 'Videollamada — se crea una sala automática.'
                  : 'Encuentro físico — el anfitrión indica la ubicación.';
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setSelectedMode(m)}
                    aria-pressed={active}
                    className={`flex-1 min-w-[140px] inline-flex flex-col items-start gap-1 rounded-md border px-3 py-2 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                      active
                        ? 'bg-gradient-to-r from-[#2563EB] to-[#06B6D4] text-white border-transparent shadow-sm'
                        : 'bg-background hover:bg-accent hover:text-accent-foreground border-input'
                    }`}
                  >
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium">
                      <Icon className="size-3.5" aria-hidden />
                      {label}
                    </span>
                    <span className={`text-[10px] ${active ? 'text-white/80' : 'text-muted-foreground'}`}>
                      {description}
                    </span>
                  </button>
                );
              })}
            </div>
            {selectedMode === 'presencial' && (
              <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                <MapPin className="size-3.5" aria-hidden />
                Ubicación: <span className="font-medium">{availability.location}</span>
              </p>
            )}
          </div>
        );
      })()}

      {/* V124.2-RESERVAS-TOGGLE-DIGITAL-PRESENCIAL — banner presencial (solo
          cuando presencial es el ÚNICO modo activo — cuando ambos activos,
          el picker de arriba ya muestra la ubicación al elegir presencial). */}
      {(() => {
        const modes = availability.modes ?? [availability.mode];
        const onlyPresencial = modes.includes('presencial') && !modes.includes('digital');
        return onlyPresencial;
      })() && (
        <div
          className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 flex items-start gap-3"
          role="status"
          aria-live="polite"
        >
          <MapPin className="size-5 text-amber-500 shrink-0 mt-0.5" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-medium text-amber-100">Esta es una reserva presencial</p>
            <p className="text-xs text-amber-200/80 mt-1">
              Ubicación: <span className="font-medium">{availability.location}</span>
            </p>
            <p className="text-xs text-amber-200/70 mt-1">
              No se creará una sala de video — te esperamos en el lugar indicado a la hora de tu reserva.
            </p>
          </div>
        </div>
      )}

      {/* Duration selector */}
      <div className="flex gap-2 mb-6">
        {availability.durations.map(d => (
          <Button
            key={d}
            variant={selectedDuration === d ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setSelectedDuration(d);
              setSelectedSlot(null);
            }}
          >
            <Clock className="size-4 mr-1" />
            {d} min
          </Button>
        ))}
      </div>

      {/* Slots grid */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="size-4" />
            Horarios disponibles
          </CardTitle>
          <CardDescription>
            Zona horaria: {availability.timezone} · Próximos {availability.maxAdvanceDays} días
          </CardDescription>
        </CardHeader>
        <CardContent>
          {slots.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No hay horarios disponibles para esta duración. Prueba con otra.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-96 overflow-y-auto">
              {slots.map((slot, idx) => {
                const isSelected = selectedSlot?.startTime === slot.startTime;
                const date = new Date(slot.startTime);
                return (
                  <button
                    key={idx}
                    onClick={() => setSelectedSlot(slot)}
                    className={`p-2 rounded-lg border text-sm text-center transition ${
                      isSelected
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'hover:bg-muted border-border'
                    }`}
                  >
                    <div className="font-medium">
                      {date.toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </div>
                    <div className="text-xs opacity-80">
                      {date.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Guest info form */}
      {selectedSlot && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tus datos</CardTitle>
            <CardDescription>
              Reserva para {new Date(selectedSlot.startTime).toLocaleString('es-CO')} ({selectedSlot.durationMinutes} min)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="name" className="text-xs">Nombre *</Label>
              <Input
                id="name"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Tu nombre"
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="email" className="text-xs">Email *</Label>
              <Input
                id="email"
                type="email"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                placeholder="tu@email.com"
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="phone" className="text-xs">WhatsApp (opcional)</Label>
              <Input
                id="phone"
                value={guestPhone}
                onChange={(e) => setGuestPhone(e.target.value)}
                placeholder="+57 300 123 4567"
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="notes" className="text-xs">Notas (opcional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="¿De qué quieres hablar?"
                rows={3}
                className="text-sm"
              />
            </div>
            <Button onClick={handleBook} disabled={booking} className="w-full">
              {booking ? <Loader2 className="size-4 animate-spin mr-2" /> : <Check className="size-4 mr-2" />}
              {booking ? 'Reservando…' : 'Confirmar reserva'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
